import React from 'react';

/**
 * ApexGlyph
 * ---------
 * The official APEX Verity brand mark.
 *
 * Geometry: a thin horizon-line that rises into a tight Gaussian bell curve
 * at the center, with a forensic V-probe arms that collapse from the baseline
 * and drop below it — the continuity line itself converging into a probe.
 *
 *                  ╱╲
 *      ___________╱  ╲___________     ← horizon + tight bell
 *                 ╲ V╱                ← V arms collapse from baseline, drop below
 *                  └┘
 *
 * The bell carries a chromatic gradient (blue ← silver → teal) that
 * differentiates the two governance domains under one signal. The V arms
 * inherit the side colors and converge into silver at the tip.
 *
 * Variants:
 *   default — full platform identity (blue ← silver → teal)
 *   lens    — cloud governance (cool blue → teal, more blue-dominant)
 *   trust   — AI governance (silver → indigo, cooler / forensic)
 *
 * Props
 *   size      — rendered HEIGHT in px (width auto = size * 2.5)
 *   variant   — 'default' | 'lens' | 'trust'
 *   blue/teal/silver — palette overrides (optional)
 *   color     — back-compat: forces monochrome
 *   indigo    — back-compat: ignored (legacy)
 *   ariaLabel — accessibility label
 */
export default function ApexGlyph({
  size = 32,
  variant = 'default',
  blue = '#5BA8E6',
  teal = '#7FD4B8',
  silver = '#E2E8F0',
  color,
  // eslint-disable-next-line no-unused-vars
  indigo,
  ariaLabel = 'APEX Verity',
}) {
  // viewBox geometry chosen so the bell + horizon read crisply at 24px height.
  const W = 300;
  const H = 120;
  const aspect = W / H; // 2.5
  const height = size;
  const width = size * aspect;

  // Unique gradient IDs per instance.
  const uid = React.useId().replace(/:/g, '');
  const idBell    = `apx-bell-${uid}`;
  const idVLeft   = `apx-vL-${uid}`;
  const idVRight  = `apx-vR-${uid}`;
  const idGlow    = `apx-glow-${uid}`;

  // Variant-aware palette resolution. Silver stays constant across all
  // variants — the apex is the proof moment, always the brightest element.
  const variantBlue =
    variant === 'trust' ? '#8B92E8' : blue;     // trust leans indigo
  const variantTeal =
    variant === 'lens'  ? '#5CC8A8' :           // lens leans blue-dominant teal
    variant === 'trust' ? '#A5B4FC' :           // trust shifts teal toward indigo-silver
    teal;

  // Monochrome fallback (back-compat with the older `color` prop usage).
  const cBlue   = color || variantBlue;
  const cTeal   = color || variantTeal;
  const cSilver = color || silver;
  const cPeak   = color || '#FFFFFF';

  // Geometry constants — Fix 3 (tighter crest) + Fix 4 (V arms from baseline)
  const BASELINE = 90;   // horizon y
  const PEAK_X   = 150;
  const PEAK_Y   = 10;   // was 15 — tighter elevation (Fix 3)
  // V probe — Fix 4: arms start AT the baseline so they read as a natural
  // collapse of the continuity line, not an overlay. Tip drops further below.
  const V_TOP_L  = { x: 133, y: BASELINE };
  const V_TOP_R  = { x: 167, y: BASELINE };
  const V_TIP    = { x: 150, y: 112 };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={width}
      height={height}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      data-testid="apex-glyph"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        {/* Fix 2 — reduced glow / luminosity on bell gradient (0.72 vs 1).
            Silver center stays at full opacity — apex is the proof moment. */}
        <linearGradient id={idBell} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={cBlue}   stopOpacity={color ? 1 : 0.25} />
          <stop offset="18%"  stopColor={cBlue}   stopOpacity={color ? 1 : 0.72} />
          <stop offset="48%"  stopColor={cSilver} stopOpacity="1" />
          <stop offset="52%"  stopColor={cSilver} stopOpacity="1" />
          <stop offset="82%"  stopColor={cTeal}   stopOpacity={color ? 1 : 0.72} />
          <stop offset="100%" stopColor={cTeal}   stopOpacity={color ? 1 : 0.25} />
        </linearGradient>

        {/* Fix 4 — V probe gets a TWO-arm gradient system:
            left arm: blue → silver at tip
            right arm: teal → silver at tip
            so the V inherits the side colors and converges into the proof point. */}
        <linearGradient id={idVLeft} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={cBlue}   stopOpacity={color ? 1 : 0.7} />
          <stop offset="100%" stopColor={cSilver} stopOpacity="1" />
        </linearGradient>
        <linearGradient id={idVRight} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={cTeal}   stopOpacity={color ? 1 : 0.7} />
          <stop offset="100%" stopColor={cSilver} stopOpacity="1" />
        </linearGradient>

        {/* Fix 2 — apex glow radial: reduced opacities by ~40% */}
        <radialGradient id={idGlow} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={cPeak} stopOpacity="0.55" />
          <stop offset="50%"  stopColor={cPeak} stopOpacity="0.12" />
          <stop offset="100%" stopColor={cPeak} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* One continuous stroke: flat horizon → rises into a TIGHTER bell at
          the center (Fix 3) → flat horizon. The tighter Bezier shoulders make
          the crest read as a controlled measurement, not a natural wave. */}
      <path
        d={`M 4 ${BASELINE}
            L 90 ${BASELINE}
            C 125 ${BASELINE}, 138 ${PEAK_Y}, ${PEAK_X} ${PEAK_Y}
            C 162 ${PEAK_Y}, 175 ${BASELINE}, 210 ${BASELINE}
            L 296 ${BASELINE}`}
        stroke={`url(#${idBell})`}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* V probe — Fix 4: TWO separate paths so each arm carries its own
          side gradient. Arms originate at the baseline, converge at the tip
          below the line. Reads as the continuity collapsing into a probe. */}
      <path
        d={`M ${V_TOP_L.x} ${V_TOP_L.y} L ${V_TIP.x} ${V_TIP.y}`}
        stroke={`url(#${idVLeft})`}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d={`M ${V_TIP.x} ${V_TIP.y} L ${V_TOP_R.x} ${V_TOP_R.y}`}
        stroke={`url(#${idVRight})`}
        strokeWidth="2.2"
        strokeLinecap="round"
      />

      {/* Fix 2 — apex glow halo and core dot, dimmer drop-shadow */}
      <circle
        cx={PEAK_X} cy={PEAK_Y} r="6"
        fill={`url(#${idGlow})`}
      />
      <circle
        cx={PEAK_X} cy={PEAK_Y} r="1.4"
        fill={cPeak}
        style={{ filter: color ? 'none' : 'drop-shadow(0 0 4px rgba(226,232,240,0.4))' }}
      />
    </svg>
  );
}
